import React, { useState } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { Phone, ChevronDown, MapPin } from 'lucide-react';
import PageShell from '@/components/PageShell';
import SEO from '@/components/SEO';
import { useLanguage } from '@/i18n/LanguageContext';
import { getCityBySlug, CITIES } from '@/data/cityContent';
import { track } from '@/lib/analytics';

const ServiceAreaCity = () => {
  const { slug } = useParams();
  const { lang, t } = useLanguage();
  const [openFaq, setOpenFaq] = useState(null);
  const city = getCityBySlug(slug);

  if (!city) return <Navigate to="/areas" replace />;
  const c = city[lang];
  const others = CITIES.filter((x) => x.slug !== slug).slice(0, 3);

  return (
    <PageShell trackPage={`/areas/${slug}`}>
      <SEO
        title={`${c.name} Bail Bonds | Amigo's Bail Bonds | ${c.county}`}
        description={c.intro.slice(0, 155)}
        path={`/areas/${slug}`}
        jsonLd={{ '@context': 'https://schema.org', '@type': 'FAQPage', mainEntity: city.en.faq.map((f) => ({ '@type': 'Question', name: f.q, acceptedAnswer: { '@type': 'Answer', text: f.a } })) }}
      />
      <div className="page-hero">
        <div className="eyebrow"><MapPin size={13} style={{ verticalAlign: 'middle', marginRight: 4 }} />{c.county}</div>
        <h1 className="section-title" data-testid="city-page-title">{c.name.toUpperCase()} {lang === 'es' ? 'FIANZAS' : 'BAIL BONDS'}</h1>
      </div>

      <section className="section section--void">
        <div className="container" style={{ maxWidth: 860 }}>
          <p className="section-subtitle mb-8">{c.intro}</p>

          <div className="info-card mb-8">
            <h3 style={{ color: 'var(--text-primary)', margin: '0 0 0.8rem' }}>{lang === 'es' ? 'Qué hacer esta noche' : 'What to do tonight'}</h3>
            <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
              {c.tonight.map((tip, i) => <li key={i} style={{ marginBottom: '0.6rem', color: 'var(--text-secondary)', fontSize: '0.92rem', paddingLeft: '1.2rem', borderLeft: '2px solid var(--gold)' }}>{tip}</li>)}
            </ul>
          </div>

          <div className="urgent-strip mb-8">
            <p>{lang === 'es' ? `¿Necesita ayuda en ${c.name} ahora?` : `Need help in ${c.name} right now?`}</p>
            <Link to="/start-a-bond" className="btn-brass" style={{ background: '#fff', color: 'var(--red)' }} data-testid="city-start-bond-cta">{t('hero.startBond')}</Link>
          </div>

          <h2 className="section-title" style={{ fontSize: '1.5rem' }}>{c.name} FAQ</h2>
          {c.faq.map((f, i) => (
            <div className="faq-item" key={i}>
              <button className="faq-question" onClick={() => setOpenFaq(openFaq === i ? null : i)} aria-expanded={openFaq === i} data-testid={`city-faq-question-${i}`}>
                <span>{f.q}</span>
                <ChevronDown size={18} className="faq-icon" style={{ transform: openFaq === i ? 'rotate(180deg)' : 'none' }} />
              </button>
              {openFaq === i && <div className="faq-answer">{f.a}</div>}
            </div>
          ))}

          <div className="text-center mt-8 mb-8">
            <a href="tel:9368282500" className="btn-red" data-testid="city-call-cta"><Phone size={16} /> {t('hero.callNow')}: (936) 828-2500</a>
          </div>

          <div className="mt-8">
            <h4 style={{ fontFamily: 'var(--font-alt)', color: 'var(--gold)', fontSize: '0.8rem', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: '0.8rem' }}>{lang === 'es' ? 'Áreas cercanas' : 'Nearby Areas'}</h4>
            <div className="flex gap-4" style={{ flexWrap: 'wrap' }}>
              {others.map((o) => <Link key={o.slug} to={`/areas/${o.slug}`} className="btn-ghost-dark" onClick={() => track('service_area_view', { city: o.slug, from: `${slug}_related` })}>{o[lang].name}</Link>)}
            </div>
          </div>
        </div>
      </section>
    </PageShell>
  );
};

export default ServiceAreaCity;
